import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Modal, Field, TextInput, Select, Textarea, FormActions, dbErrorMessage } from './form';

const OFFER_KINDS = ['subscription', 'package', 'hourly', 'one_off'] as const;
const BILLING_PERIODS = ['monthly', 'quarterly', 'yearly', 'one_off'] as const;
const PRICING_MODELS = ['flat', 'per_equipment', 'per_area'] as const;
const PROPERTY_TYPES = ['villa', 'apartment', 'house', 'restaurant', 'bar', 'hotel', 'building', 'other'] as const;
const CURRENCIES = ['IDR', 'EUR', 'USD'] as const;

export type OfferRow = {
  id: string; name: string; kind: string; property_type: string | null; service_line_id: string | null;
  billing_period: string | null; included_visits: number | null; pricing_model: string; active: boolean; description?: string | null;
};
type PriceRow = { id?: string; currency: string; base_price: string; unit_price: string };
type SvcLine = { id: string; code: string; name_i18n: Record<string, string> };
type Base<T> = { open: boolean; onClose: () => void; onSaved: () => void; initial?: T | null };

export function OfferForm({ open, onClose, onSaved, initial }: Base<OfferRow>) {
  const { t, i18n } = useTranslation();
  const { id: orgId } = useOrg();
  const [svc, setSvc] = useState<SvcLine[]>([]);
  const [f, setF] = useState({ name: '', kind: 'subscription', property_type: '', service_line_id: '', billing_period: 'monthly', included_visits: '', pricing_model: 'flat', active: true, description: '' });
  const [prices, setPrices] = useState<PriceRow[]>([{ currency: 'IDR', base_price: '', unit_price: '' }]);
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return; setErr({});
    (async () => {
      const sl = await supabase.from('service_lines').select('id,code,name_i18n').is('deleted_at', null).order('code');
      setSvc((sl.data as SvcLine[]) ?? []);
      setF({
        name: initial?.name ?? '', kind: initial?.kind ?? 'subscription', property_type: initial?.property_type ?? '', service_line_id: initial?.service_line_id ?? '',
        billing_period: initial?.billing_period ?? 'monthly', included_visits: initial?.included_visits != null ? String(initial.included_visits) : '',
        pricing_model: initial?.pricing_model ?? 'flat', active: initial?.active ?? true, description: initial?.description ?? '',
      });
      if (initial?.id) {
        const pr = await supabase.from('catalogue_offer_prices').select('id,currency,base_price,unit_price').eq('offer_id', initial.id).is('deleted_at', null);
        const rows = ((pr.data as any[]) ?? []).map((p) => ({ id: p.id, currency: p.currency, base_price: String(p.base_price), unit_price: String(p.unit_price) }));
        setPrices(rows.length ? rows : [{ currency: 'IDR', base_price: '', unit_price: '' }]);
      } else setPrices([{ currency: 'IDR', base_price: '', unit_price: '' }]);
    })();
  }, [open, initial]);

  const slName = (s: SvcLine) => s.name_i18n?.[i18n.language] ?? s.name_i18n?.en ?? s.code;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!f.name.trim()) { setErr({ name: t('form.errRequired') }); return; }
    setBusy(true);
    const payload = {
      name: f.name.trim(), kind: f.kind, property_type: f.property_type || null, service_line_id: f.service_line_id || null,
      billing_period: f.kind === 'subscription' ? f.billing_period : (f.billing_period || null), included_visits: f.included_visits ? Number(f.included_visits) : null,
      pricing_model: f.pricing_model, active: f.active, description: f.description.trim() || null,
    };
    const saved = initial?.id
      ? await supabase.from('catalogue_offers').update(payload).eq('id', initial.id).select('id').single()
      : await supabase.from('catalogue_offers').insert({ ...payload, organization_id: orgId } as any).select('id').single();
    if (saved.error) { setBusy(false); setErr({ _: dbErrorMessage(t, saved.error) }); return; }
    const offerId = (saved.data as any).id;
    // sync prices: delete removed, upsert current
    const valid = prices.filter((p) => p.base_price !== '' || p.unit_price !== '');
    const existing = await supabase.from('catalogue_offer_prices').select('id').eq('offer_id', offerId).is('deleted_at', null);
    const keepIds = new Set(valid.map((p) => p.id).filter(Boolean));
    for (const ex of ((existing.data as any[]) ?? [])) if (!keepIds.has(ex.id)) await supabase.from('catalogue_offer_prices').delete().eq('id', ex.id);
    for (const p of valid) {
      const row = { organization_id: orgId, offer_id: offerId, currency: p.currency, base_price: Number(p.base_price) || 0, unit_price: Number(p.unit_price) || 0 };
      if (p.id) await supabase.from('catalogue_offer_prices').update(row).eq('id', p.id);
      else await supabase.from('catalogue_offer_prices').insert(row as any);
    }
    setBusy(false);
    onSaved(); onClose();
  }

  const perUnit = f.pricing_model !== 'flat';

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('cat.editOffer') : t('cat.newOffer')} wide>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.name')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
          <Field label={t('cat.kind')}>
            <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{OFFER_KINDS.map((k) => <option key={k} value={k}>{t('cat.kind_' + k)}</option>)}</Select>
          </Field>
          <Field label={t('crm.propertyType')}>
            <Select value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })}><option value="">{t('cat.anyType')}</option>{PROPERTY_TYPES.map((p) => <option key={p} value={p}>{t('propertyType.' + p)}</option>)}</Select>
          </Field>
          <Field label={t('nav.dueDates') && t('cat.serviceLine')}>
            <Select value={f.service_line_id} onChange={(e) => setF({ ...f, service_line_id: e.target.value })}><option value="">—</option>{svc.map((s) => <option key={s.id} value={s.id}>{slName(s)}</option>)}</Select>
          </Field>
          {f.kind === 'subscription' && <>
            <Field label={t('cat.billingPeriod')}>
              <Select value={f.billing_period} onChange={(e) => setF({ ...f, billing_period: e.target.value })}>{BILLING_PERIODS.map((b) => <option key={b} value={b}>{t('cat.period_' + b)}</option>)}</Select>
            </Field>
            <Field label={t('cat.includedVisits')}><TextInput type="number" min="0" value={f.included_visits} onChange={(e) => setF({ ...f, included_visits: e.target.value })} /></Field>
          </>}
          <Field label={t('cat.pricingModel')}>
            <Select value={f.pricing_model} onChange={(e) => setF({ ...f, pricing_model: e.target.value })}>{PRICING_MODELS.map((m) => <option key={m} value={m}>{t('cat.model_' + m)}</option>)}</Select>
          </Field>
          <Field label={t('crm.active')}>
            <Select value={f.active ? '1' : '0'} onChange={(e) => setF({ ...f, active: e.target.value === '1' })}><option value="1">{t('common.yes')}</option><option value="0">{t('common.no')}</option></Select>
          </Field>
        </div>

        <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted">{t('cat.prices')}{perUnit ? ` — ${t('cat.perUnitHint')}` : ''}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setPrices([...prices, { currency: 'IDR', base_price: '', unit_price: '' }])}>+ {t('cost.add')}</Button>
          </div>
          <div className="grid grid-cols-[110px_1fr_1fr_auto] items-center gap-2 text-2xs text-muted">
            <span>{t('cat.currency')}</span><span>{t('cat.basePrice')}</span><span className={cn(!perUnit && 'opacity-40')}>{t('cat.unitPrice')}</span><span />
          </div>
          {prices.map((p, i) => (
            <div key={i} className="grid grid-cols-[110px_1fr_1fr_auto] items-center gap-2">
              <Select value={p.currency} onChange={(e) => setPrices(prices.map((x, j) => j === i ? { ...x, currency: e.target.value } : x))}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</Select>
              <TextInput type="number" min="0" step="0.01" value={p.base_price} onChange={(e) => setPrices(prices.map((x, j) => j === i ? { ...x, base_price: e.target.value } : x))} />
              <TextInput type="number" min="0" step="0.01" disabled={!perUnit} value={p.unit_price} onChange={(e) => setPrices(prices.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} />
              <button type="button" onClick={() => setPrices(prices.filter((_, j) => j !== i))} className="px-2 text-muted hover:text-st-overdue-fg">✕</button>
            </div>
          ))}
        </div>

        <Field label={t('form.notes')}><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}
